import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { FilterComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterDataRange } from '@/app/common/filters/core/filtersTypes.ts';
import { getRangeBounds, getRangeRoundTo, hasDiagram } from '@/app/common/filters/types/range/rangeData.ts';
import { RangeSlider, type RangeValue } from '@/app/common/filters/types/range/RangeSlider.tsx';
import { BudgetRangeInput } from '@/app/common/filters/types/range/BudgetRangeInput.tsx';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { usePriceSliderThumbs } from '@/app/common/filters/types/range/usePriceSliderThumbs.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';

const PEAKS_HEIGHT = 50;
// A bucket holding even one property stays visible; an empty one draws nothing at all.
const PEAK_MIN_HEIGHT = 1;
// The slider centres a 4px track in a 28px row, and the histogram stands on the track's top edge.
const SLIDER_OVERLAP = 12;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space200};
  padding-bottom: ${({ theme }) => theme.kallisto.space.space100};
`;

const Graph = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Peaks = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1px;
  height: ${PEAKS_HEIGHT}px;
  padding: 0 ${({ theme }) => theme.kallisto.space.space100};
  margin-bottom: -${SLIDER_OVERLAP}px;
`;

const Peak = styled.div<{ $height: number; $selected: boolean }>`
  flex: 1 0 0;
  min-width: 1px;
  height: ${({ $height }) => $height}px;
  border-radius: 1px 1px 0 0;
  background-color: ${({ theme, $selected }) =>
    $selected
      ? theme.kallisto.color.background.interactive.backgroundPrimaryDefault
      : theme.kallisto.color.background.static.backgroundDisabled};
`;

const Inputs = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

const Separator = styled.span`
  ${({ theme }) => theme.kallisto.mixins.font('Body')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
`;

export const BudgetRange = ({
  filter,
  handleFilterChanged,
  // The granularity the slider moves by, in the unit the caller renders.
  roundTo,
  sectionTitle,
}: FilterComponentProps & { roundTo?: number }) => {
  const { localization } = useFiltersUiData();
  const { t } = useTranslation();
  const thumbs = usePriceSliderThumbs(filter, sectionTitle);

  const data = filter.data as FilterDataRange;
  const { from, to } = getRangeBounds(data);
  const { min, max } = data.value;

  const [dragValue, setDragValue] = useState<RangeValue>({ min, max });
  // Asking again for the range already asked for would run the same search twice.
  const requestedRef = useRef<RangeValue>({ min, max });
  useEffect(() => {
    requestedRef.current = { min, max };
    setDragValue({ min, max });
  }, [min, max]);

  // Deliberately not `isLoading`: disabling a focused field mid-refresh drops the half-typed amount.
  const isDisabled = !!filter.disabled || from === to;

  const clamp = (value: number) => Math.min(Math.max(value, from), to);

  const commit = (value: RangeValue) => {
    const next = { min: clamp(value.min), max: clamp(value.max) };
    // The fields read the drag, so they have to land on the range actually asked for, clamps included.
    setDragValue(next);
    if (next.min === requestedRef.current.min && next.max === requestedRef.current.max) return;
    requestedRef.current = next;
    handleFilterChanged([{ ...filter, data: { ...data, value: next } }]);
  };

  // Read from the last range asked for, so a commit still in flight cannot undo the other bound.
  const changeMin = (digits: string) => {
    const upper = clamp(requestedRef.current.max);
    commit({ min: Math.min(digits ? clamp(Number(digits)) : from, upper), max: upper });
  };
  const changeMax = (digits: string) => {
    const lower = clamp(requestedRef.current.min);
    commit({ min: lower, max: Math.max(digits ? clamp(Number(digits)) : to, lower) });
  };

  const diagram = hasDiagram(data.dynamicValues) ? data.dynamicValues.diagram : undefined;
  const tallest = diagram ? Math.max(...diagram) : 0;
  const bucketWidth = diagram ? (to - from) / diagram.length : 0;

  const format = (price: number) => formatPrice({ price, localization });
  const atUpperBound = dragValue.max >= to;

  const step = roundTo ?? getRangeRoundTo(data);

  // The slider stops on the last multiple of its step, short of an off-grid top, so reaching that
  // last one counts as reaching the top. Coming back down from the top is a move, not a shortfall.
  const commitFromSlider = ({ min: dragged, max: draggedMax }: RangeValue) => {
    const raised = draggedMax > requestedRef.current.max;
    commit({ min: dragged, max: raised && draggedMax > to - step ? to : draggedMax });
  };

  return (
    <Container>
      <Graph>
        {diagram && (
          <Peaks aria-hidden="true" data-testid="budget-peaks">
            {diagram.map((count, index) => {
              const bucketStart = from + index * bucketWidth;
              // A disabled histogram reads as inert, so nothing in it is highlighted.
              const selected = !isDisabled && bucketStart + bucketWidth > dragValue.min && bucketStart < dragValue.max;
              return (
                <Peak
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  $height={count === 0 ? 0 : Math.max(PEAK_MIN_HEIGHT, (count / tallest) * PEAKS_HEIGHT)}
                  $selected={selected}
                  data-selected={selected}
                />
              );
            })}
          </Peaks>
        )}
        <RangeSlider
          // The committed range, not the live drag, so a missed commit is still detectable.
          value={{ min, max }}
          from={from}
          to={to}
          disabled={isDisabled}
          step={step}
          onDrag={setDragValue}
          onCommit={commitFromSlider}
          thumbNames={thumbs.thumbNames}
          valueText={thumbs.valueText}
        />
      </Graph>
      <Inputs>
        <BudgetRangeInput
          label={t('vacation.oss.filters.budget.min_price')}
          displayValue={format(dragValue.min)}
          editValue={String(dragValue.min)}
          disabled={isDisabled}
          onValueChange={changeMin}
        />
        <Separator aria-hidden="true">–</Separator>
        <BudgetRangeInput
          label={t('vacation.oss.filters.budget.max_price')}
          displayValue={
            atUpperBound ? t('vacation.oss.components.searchfilters.labels.nolimit') : format(dragValue.max)
          }
          editValue={atUpperBound ? '' : String(dragValue.max)}
          disabled={isDisabled}
          onValueChange={changeMax}
        />
      </Inputs>
    </Container>
  );
};
